import { Controller } from '@hotwired/stimulus'
import {
  abreElPasoDelWizard,
  cierraElPasoDelWizard,
  emitePasoCompletado,
  emitePasoSaltado
} from 'lib/analitica_del_wizard'

// Analítica del wizard de personalización (PRD-134-1, TC-134-1.2.3.4).
//
// Se monta en el contenedor del wizard (`onboarding/personalizacion/show.html.haml`), que envuelve
// los dos formularios del pie, y engancha los tres momentos que emiten:
//
//   1. al conectar, el paso se ha pintado    → `welcome_step_viewed`
//   2. `submit` del formulario del paso      → `welcome_step_completed` + nivel de pregunta o
//                                              `welcome_categories_selected` en newsletters,
//                                              salvo si lo dispara "← Atrás" (no emite nada)
//   3. `submit` del formulario de "Saltar"   → `welcome_step_skipped` + nivel de pregunta
//
// Los dos envíos DESCARGAN la página en el mismo gesto, así que el push va en el manejador del
// `submit` y no después: un evento emitido después no llegaría a salir.
//
// Qué evento sale y con qué parámetros lo decide `lib/analitica_del_wizard`; lo que hace este
// controlador es leer del DOM lo que la persona ha marcado.
export default class AnaliticaWizardController extends Controller {
  static targets = ['atras']
  static values = { contexto: Object }

  connect () {
    abreElPasoDelWizard(this.contextoValue)
  }

  disconnect () {
    cierraElPasoDelWizard()
  }

  // `pageshow` de la ventana. Quien vuelve al paso con el botón "atrás" del navegador puede recibir
  // la página tal como la dejó, sin recargarla (`persisted`): el controlador no se vuelve a conectar
  // y el paso seguiría resuelto, así que lo que hiciera ahora no se contaría. Es otra vista del
  // paso: se abre de nuevo. En una carga normal no hace nada, porque ya lo abrió `connect`.
  vuelve (evento) {
    if (evento.persisted) abreElPasoDelWizard(this.contextoValue)
  }

  // "← Atrás" envía el mismo formulario que "Continuar" —guarda lo marcado antes de volver—, pero
  // volver no es completar el paso: el completado sale cuando se continúa.
  completa (evento) {
    if (this.hasAtrasTarget && evento?.submitter === this.atrasTarget) return

    emitePasoCompletado({ valores: this.respuestasMarcadas(), categorias: this.categoriasMarcadas() })
  }

  salta () {
    emitePasoSaltado()
  }

  // Las respuestas marcadas de la pregunta del paso, en el orden en que están pintadas —que es el
  // del catálogo, porque el servidor las pinta desde él—.
  //
  // Se buscan por el nombre de la PREGUNTA porque `question` es a la vez el nombre del campo del
  // formulario y el de la columna donde se persiste (`event_interest`): así el evento no puede leer
  // un campo distinto del que se guarda. Los dos nombres —con `[]` y sin él— cubren casillas y
  // radios. El código postal del paso de eventos no se lee nunca: no tiene el nombre de la pregunta.
  respuestasMarcadas () {
    const { question } = this.contextoValue
    if (!question) return []

    const marcados = this.element.querySelectorAll(
      `input[name="${question}"]:checked, input[name="${question}[]"]:checked`
    )

    return [...marcados].map((campo) => campo.value)
  }

  // Los ids de las temáticas marcadas en el paso de newsletters. El chip viaja al servidor por permalink, pero
  // `welcome_categories_selected` emite `tematicas.id`, que la vista deja en `data-categoria-id`.
  categoriasMarcadas () {
    const marcadas = this.element.querySelectorAll('input[data-categoria-id]:checked')

    return [...marcadas].map((chip) => chip.dataset.categoriaId)
  }
}
